import React, { useState, useCallback } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  TrashIcon,
  BankIcon,
  PlusIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import { useGetIntegration } from 'src/api/rpc/hooks/integrations/useIntegrations';
import { orpc } from 'src/api/rpc/client';
import { useMutation } from '@tanstack/react-query';
import {
  IntegrationAvailability,
  IntegrationKey,
  PlaidBankAccount,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnExit,
  PlaidLinkError,
} from 'react-plaid-link';
import { formatDateTime } from '@erp/shared-utils';

export function PlaidIntegrationSettings({
  availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const isAvailable = availability?.available ?? true;
  const unavailableReason = availability?.reason;
  const { showToast } = useToast();
  const {
    data: integration,
    isLoading,
    error,
    refetch,
  } = useGetIntegration(IntegrationKey.Plaid);

  // Plaid-specific mutations defined in component
  const createLinkToken = useMutation(
    orpc.integrations.plaid.createLinkToken.mutationOptions(),
  );
  const exchangePublicToken = useMutation(
    orpc.integrations.plaid.exchangePublicToken.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const deleteBankAccount = useMutation(
    orpc.integrations.plaid.deleteBankAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const createUpdateLinkToken = useMutation(
    orpc.integrations.plaid.createUpdateLinkToken.mutationOptions(),
  );

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isInitializingLink, setIsInitializingLink] = useState(false);
  const [reauthenticatingAccountId, setReauthenticatingAccountId] = useState<
    string | null
  >(null);

  // Initialize Plaid Link
  const handleInitializePlaidLink = async () => {
    if (!isAvailable) {
      showToast({
        title: 'Integration Unavailable',
        message: unavailableReason || 'Plaid is not available right now.',
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsInitializingLink(true);
    try {
      const result = await createLinkToken.mutateAsync(undefined);
      setLinkToken(result.linkToken);
    } catch (error) {
      showToast({
        title: 'Failed to Initialize',
        message: 'Unable to initialize Plaid Link. Please try again.',
        color: 'error',
        icon: 'error',
      });
      setIsInitializingLink(false);
    }
  };

  // Plaid Link success handler
  const onSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        // If we're re-authenticating, just refetch the integration
        // The access token is automatically updated by Plaid
        if (reauthenticatingAccountId) {
          await refetch();
          showToast({
            title: 'Re-authentication Successful',
            message: `Successfully re-authenticated ${
              metadata.institution?.name || 'your bank account'
            }.`,
            color: 'success',
            icon: 'check',
          });
        } else {
          // Normal connection flow
          await exchangePublicToken.mutateAsync({
            publicToken,
            institutionName: metadata.institution?.name || 'Unknown Bank',
            institutionId: metadata.institution?.institution_id || '',
            accountIds: metadata.accounts.map((acc) => acc.id),
          });

          showToast({
            title: 'Bank Account Connected',
            message: `Successfully connected ${
              metadata.institution?.name || 'your bank account'
            }.`,
            color: 'success',
            icon: 'check',
          });
        }

        // Clear link token after successful connection
        setLinkToken(null);
      } catch (error) {
        showToast({
          title: reauthenticatingAccountId
            ? 'Re-authentication Failed'
            : 'Connection Failed',
          message: reauthenticatingAccountId
            ? 'Failed to re-authenticate bank account. Please try again.'
            : 'Failed to save bank account connection. Please try again.',
          color: 'error',
          icon: 'error',
        });
      } finally {
        setIsInitializingLink(false);
        setReauthenticatingAccountId(null);
      }
    },
    [exchangePublicToken, showToast, reauthenticatingAccountId, refetch],
  );

  // Plaid Link exit handler
  const onExit: PlaidLinkOnExit = useCallback(
    (error: PlaidLinkError | null) => {
      if (error) {
        console.error('Plaid Link error:', error);
        showToast({
          title: reauthenticatingAccountId
            ? 'Re-authentication Cancelled'
            : 'Connection Cancelled',
          message:
            error.display_message ||
            (reauthenticatingAccountId
              ? 'Bank account re-authentication was cancelled.'
              : 'Bank account connection was cancelled.'),
          color: 'warning',
          icon: 'warning',
        });
      }
      setLinkToken(null);
      setIsInitializingLink(false);
      setReauthenticatingAccountId(null);
    },
    [showToast, reauthenticatingAccountId],
  );

  const { open: openPlaidLink, ready: plaidLinkReady } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Handle "Connect Bank Account" button click
  const handleConnectBankAccount = async () => {
    await handleInitializePlaidLink();
  };

  // When link token is set and Plaid Link is ready, open it automatically
  React.useEffect(() => {
    if (linkToken && plaidLinkReady) {
      openPlaidLink();
    }
  }, [linkToken, plaidLinkReady, openPlaidLink]);

  // Handle re-authenticate bank account
  const handleReauthenticateAccount = useCallback(
    async (accountId: string) => {
      if (!isAvailable) {
        showToast({
          title: 'Integration Unavailable',
          message: unavailableReason || 'Plaid is not available right now.',
          color: 'warning',
          icon: 'warning',
        });
        return;
      }

      setReauthenticatingAccountId(accountId);
      try {
        const result = await createUpdateLinkToken.mutateAsync({ accountId });
        setLinkToken(result.linkToken);
      } catch (error) {
        showToast({
          title: 'Failed to Initialize',
          message: 'Unable to initialize re-authentication. Please try again.',
          color: 'error',
          icon: 'error',
        });
        setReauthenticatingAccountId(null);
      }
    },
    [createUpdateLinkToken, isAvailable, unavailableReason, showToast],
  );

  // Handle delete bank account
  const handleDeleteAccount = async (accountId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect this bank account? This action cannot be undone.',
      )
    ) {
      return;
    }

    try {
      await deleteBankAccount.mutateAsync({ accountId });

      showToast({
        title: 'Account Disconnected',
        message: 'Bank account has been successfully disconnected.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Disconnection Failed',
        message: 'Failed to disconnect bank account. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>Loading integration settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          Failed to load integration settings. Please try refreshing the page.
        </AlertDescription>
      </AlertContainer>
    );
  }

  const accounts =
    integration?.key === IntegrationKey.Plaid
      ? integration.settings.accounts
      : [];
  const hasAccounts = accounts.length > 0;

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="font-medium">Integration Active</span>
          </div>
          <Badge
            variant="outline"
            className="bg-green-100 text-green-700 border-green-300"
          >
            {hasAccounts
              ? `${accounts.length} Account${
                  accounts.length !== 1 ? 's' : ''
                } Connected`
              : 'Ready to Connect'}
          </Badge>
          {!isAvailable && (
            <Badge
              variant="outline"
              className="border-red-300 text-red-700 bg-red-50"
            >
              Unavailable
            </Badge>
          )}
        </div>
        {!isAvailable && (
          <p className="text-sm text-red-600 mt-2">
            {unavailableReason || 'Plaid is not available right now.'}
          </p>
        )}
      </div>

      <Separator />

      {/* Connected Bank Accounts */}
      {hasAccounts ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Connected Bank Accounts</CardTitle>
            <CardDescription>
              Manage your connected bank accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {accounts.map((account: PlaidBankAccount) => (
              <Card key={account.id} className="border-2">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <BankIcon className="h-5 w-5 text-blue-700" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div>
                          <h4 className="font-semibold text-base">
                            {account.institutionName}
                          </h4>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Accounts: </span>
                          <span className="text-gray-600">
                            {account.accountIds.length} account
                            {account.accountIds.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Connected on {formatDateTime(account.connectedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReauthenticateAccount(account.id)}
                        disabled={
                          !isAvailable ||
                          reauthenticatingAccountId === account.id ||
                          createUpdateLinkToken.isPending
                        }
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Re-authenticate this bank account"
                      >
                        {reauthenticatingAccountId === account.id ? (
                          <CircleNotchIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <ArrowsClockwiseIcon className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAccount(account.id)}
                        disabled={deleteBankAccount.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {deleteBankAccount.isPending ? (
                          <CircleNotchIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Connect Another Account Button */}
            <div className="pt-2">
              <Button
                onClick={handleConnectBankAccount}
                disabled={isInitializingLink || !isAvailable}
                variant="outline"
                className="w-full"
              >
                {isInitializingLink ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Connect Another Bank Account
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* No Accounts Connected - Initial Setup */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Connect Your First Bank Account
            </CardTitle>
            <CardDescription>
              Securely connect a bank account through Plaid to enable payments
              and reconciliation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <BankIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                No bank accounts connected
              </h3>
              <p className="text-gray-600 mb-3">
                Connect your bank account to start managing vendor payments and
                automated reconciliation.
              </p>
              {!isAvailable && (
                <p className="text-sm text-red-600 mb-4">
                  {unavailableReason || 'Plaid is not available right now.'}
                </p>
              )}
              <Button
                size="lg"
                className="px-8"
                onClick={handleConnectBankAccount}
                disabled={isInitializingLink || !isAvailable}
              >
                {isInitializingLink ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <BankIcon className="h-4 w-4 mr-2" />
                    Connect Bank Account
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
